import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

// ─── Types ───

export interface DetailPanelInfo {
  type: 'issue' | 'requirement' | 'spec' | 'testcase';
  itemId: string;
  metadata?: unknown;
}

interface PanelState {
  chatPanelEnabled: boolean;
  chatPanelWidth: number;
  detailPanel: DetailPanelInfo | null;
}

interface PanelContextValue extends PanelState {
  toggleChatPanel: () => void;
  setChatPanelWidth: (width: number) => void;
  openDetailPanel: (type: DetailPanelInfo['type'], itemId: string, metadata?: unknown) => void;
  closeDetailPanel: () => void;
  // 계산 속성
  isChatVisible: boolean;
  isDetailVisible: boolean;
  activeSidePanel: 'chat' | 'detail' | null;
}

// ─── localStorage keys ───

const CHAT_PANEL_KEY = (projectId: string) => `chatPanel:${projectId}`;
const CHAT_WIDTH_KEY = (projectId: string) => `chatPanelWidth:${projectId}`;

const DEFAULT_CHAT_WIDTH = 400;

// ─── Context ───

const PanelContext = createContext<PanelContextValue | null>(null);

export function usePanelContext() {
  const ctx = useContext(PanelContext);
  if (!ctx) {
    throw new Error('usePanelContext must be used within PanelProvider');
  }
  return ctx;
}

// ─── Provider ───

export function PanelProvider({ projectId, children }: { projectId: string; children: ReactNode }) {
  // localStorage에서 초기값 로드
  const [chatPanelEnabled, setChatPanelEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem(CHAT_PANEL_KEY(projectId));
      return stored === 'true';
    } catch {
      return false;
    }
  });

  const [chatPanelWidth, setChatPanelWidthState] = useState(() => {
    try {
      const stored = localStorage.getItem(CHAT_WIDTH_KEY(projectId));
      return stored ? Number(stored) : DEFAULT_CHAT_WIDTH;
    } catch {
      return DEFAULT_CHAT_WIDTH;
    }
  });

  const [detailPanel, setDetailPanel] = useState<DetailPanelInfo | null>(null);
  // 초기 마운트 시점은 "프로젝트 변경"이 아니므로 분리 (마운트 시 detailPanel 강제 닫기 방지)
  const prevProjectIdRef = useRef(projectId);

  // 상세 패널 상태를 URL의 ?panel=<itemId>[&panelType=<type>] 와 양방향 동기화 →
  // 브라우저 뒤로가기/앞으로가기로 패널 열림 상태가 복원됨.
  // metadata가 필요한 testcase 타입은 URL로 표현 불가하므로 동기화에서 제외.
  const [searchParams, setSearchParams] = useSearchParams();
  const detailPanelRef = useRef(detailPanel);
  detailPanelRef.current = detailPanel;

  // projectId 변경 시 해당 프로젝트의 저장값 로드
  useEffect(() => {
    try {
      const storedEnabled = localStorage.getItem(CHAT_PANEL_KEY(projectId));
      setChatPanelEnabled(storedEnabled === 'true');

      const storedWidth = localStorage.getItem(CHAT_WIDTH_KEY(projectId));
      setChatPanelWidthState(storedWidth ? Number(storedWidth) : DEFAULT_CHAT_WIDTH);
    } catch {
      setChatPanelEnabled(false);
      setChatPanelWidthState(DEFAULT_CHAT_WIDTH);
    }

    // 프로젝트가 실제로 바뀌었을 때만 상세 패널 닫기 (마운트 시점에는 자식이 openDetailPanel을 호출했을 수 있으므로 덮어쓰지 않음)
    if (prevProjectIdRef.current !== projectId) {
      setDetailPanel(null);
      prevProjectIdRef.current = projectId;
    }
  }, [projectId]);

  // chatPanelEnabled 변경 → localStorage 저장
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_PANEL_KEY(projectId), String(chatPanelEnabled));
    } catch {
      // silent
    }
  }, [projectId, chatPanelEnabled]);

  // ─── Actions ───

  const toggleChatPanel = useCallback(() => {
    setChatPanelEnabled((prev) => {
      if (prev) {
        // off로 전환: detailPanel 유지
        return false;
      } else {
        // on으로 전환: 상세 패널이 열려있으면 닫기
        setDetailPanel(null);
        return true;
      }
    });
  }, []);

  const setChatPanelWidth = useCallback(
    (width: number) => {
      setChatPanelWidthState(width);
      try {
        localStorage.setItem(CHAT_WIDTH_KEY(projectId), String(width));
      } catch {
        // silent
      }
    },
    [projectId],
  );

  const openDetailPanel = useCallback((type: DetailPanelInfo['type'], itemId: string, metadata?: unknown) => {
    // metadata가 있는 패널(testcase 등)은 URL로 직렬화 불가 → state로만 처리
    if (metadata !== undefined) {
      setDetailPanel({ type, itemId, metadata });
      return;
    }
    // URL 쿼리에 push → 뒤로가기로 복원 가능
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('panel', itemId);
      if (type !== 'issue') next.set('panelType', type);
      else next.delete('panelType');
      return next;
    });
  }, [setSearchParams]);

  const closeDetailPanel = useCallback(() => {
    // metadata 기반 패널은 URL에 없으므로 state만 정리
    if (detailPanelRef.current?.metadata !== undefined) {
      setDetailPanel(null);
      return;
    }
    // URL 파라미터 제거 (replace — 닫기 동작이 새 히스토리 항목을 만들지 않도록)
    setSearchParams((prev) => {
      if (!prev.has('panel')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('panel');
      next.delete('panelType');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // URL ?panel → state 동기화 (back/forward 처리 + 외부 진입)
  useEffect(() => {
    const panelId = searchParams.get('panel');
    const panelType = (searchParams.get('panelType') as DetailPanelInfo['type'] | null) ?? 'issue';
    if (panelId) {
      setDetailPanel((prev) => {
        if (prev?.itemId === panelId && prev.type === panelType && prev.metadata === undefined) return prev;
        return { type: panelType, itemId: panelId };
      });
    } else {
      setDetailPanel((prev) => {
        // metadata 기반 패널은 URL 영향 받지 않음
        if (prev?.metadata !== undefined) return prev;
        return prev ? null : prev;
      });
    }
  }, [searchParams]);

  // ─── 계산 속성 ───

  const isChatVisible = chatPanelEnabled && detailPanel === null;
  const isDetailVisible = detailPanel !== null;
  const activeSidePanel: 'chat' | 'detail' | null = isDetailVisible
    ? 'detail'
    : isChatVisible
      ? 'chat'
      : null;

  const value = useMemo<PanelContextValue>(
    () => ({
      chatPanelEnabled,
      chatPanelWidth,
      detailPanel,
      toggleChatPanel,
      setChatPanelWidth,
      openDetailPanel,
      closeDetailPanel,
      isChatVisible,
      isDetailVisible,
      activeSidePanel,
    }),
    [
      chatPanelEnabled,
      chatPanelWidth,
      detailPanel,
      toggleChatPanel,
      setChatPanelWidth,
      openDetailPanel,
      closeDetailPanel,
      isChatVisible,
      isDetailVisible,
      activeSidePanel,
    ],
  );

  return <PanelContext.Provider value={value}>{children}</PanelContext.Provider>;
}
